/**
 * Notes Service — Unified notes across deals, faults, service requests, and jobs.
 *
 * Replaces three legacy patterns:
 *   - Deal.dealNotes[] (array on the deal doc)
 *   - Job.notes (single string)
 *   - Job.schedulingNotes (single string + author/timestamp fields)
 *
 * One Firestore collection (`notes`), one composer, one panel. Notes link
 * to an entity (deal/fault/service_request/job). When the scheduler writes a
 * note against a deal/fault/SR, the `jobId` is also stamped so the queue card
 * can preview the latest note without an extra parent lookup.
 */

import { db } from '@/firebase';
import {
  collection,
  addDoc,
  updateDoc,
  increment,
  doc,
  query,
  where,
  onSnapshot,
  getDocs,
} from 'firebase/firestore';
import type { Job, Note, NoteEntityType, NoteSource } from '@/types';
import { MentionEntityType } from '@/types/mention.types';
import type { MentionableUser } from '@/types/mention.types';
import {
  createDirectMentions,
  createMentions,
  extractMentions,
} from './mention.service';
import { tenantIdReadUnion } from '@/lib/tenant';

// Threading helpers are PURE and live in @/utils/note-threads so timeline
// entry builders (unit-tested as plain data functions) can group replies
// without pulling the Firebase SDK in through this module. Re-exported here
// because this is where every notes consumer already looks.
export {
  buildNoteThreads,
  rootNotesOnly,
  threadParticipants,
  type NoteThread,
} from '@/utils/note-threads';

/**
 * Map a NoteEntityType to the corresponding MentionEntityType so @mentions
 * inside a note route to the right entity feed.
 */
function toMentionEntityType(t: NoteEntityType): MentionEntityType {
  switch (t) {
    case 'deal':
      return MentionEntityType.DEAL;
    case 'fault':
      return MentionEntityType.FAULT;
    case 'service_request':
      return MentionEntityType.SERVICE_REQUEST;
    case 'job':
      return MentionEntityType.JOB;
    case 'customer':
      return MentionEntityType.CUSTOMER;
  }
}

/**
 * Build the in-app link for a note's parent entity. Used by mention
 * notifications so a tagged user can click straight to the source.
 */
function linkPathFor(entityType: NoteEntityType, entityId: string, jobId?: string): string {
  switch (entityType) {
    case 'deal':
      return `/home/deals/${entityId}`;
    case 'fault':
      // FaultPipelinePage reads `?openId=` to auto-open the detail modal.
      return `/home/faults?openId=${entityId}`;
    case 'service_request':
      // ServiceRequestsPage reads `?openId=` to auto-open the detail modal.
      return `/home/service-requests?openId=${entityId}`;
    case 'job':
      // Jobs don't have a stable detail route — the scheduler is the natural
      // landing pad. Use jobId if provided (always true for entityType=job).
      return `/home/schedule?jobId=${jobId || entityId}`;
    case 'customer':
      // CustomersPage reads `?openId=` to auto-open the customer detail modal.
      return `/home/customers?openId=${entityId}`;
  }
}

// ============================================================================
// CREATE
// ============================================================================

export interface CreateNoteInput {
  tenantId: string;
  entityType: NoteEntityType;
  entityId: string;
  jobId?: string;
  content: string;
  source: NoteSource;
  createdBy: string;
  createdByName: string;
  entityTitle?: string;       // For mention notifications, e.g. "Deal: John Smith"
  availableUsers?: MentionableUser[]; // Required if you want @mentions fanned out
  pinned?: boolean;
  /** Set to make this note a reply to another note in the same entity. */
  parentNoteId?: string;
}

/**
 * Create a note + fan out mention notifications.
 *
 * The `availableUsers` list is required only when the note may contain
 * @mentions you want to notify; callers without that context (server/bg
 * tasks) can omit it and notes are written without mention fanout.
 */
export async function createNote(input: CreateNoteInput): Promise<string> {
  const now = new Date().toISOString();

  // Extract mention userIds for storage on the note itself (used by UI
  // highlighting + audit). Mention notifications are a separate write.
  const mentionedUserIds = input.availableUsers
    ? Array.from(
        new Set(
          extractMentions(input.content, input.availableUsers).map((m) => m.userId),
        ),
      )
    : [];

  const noteDoc: Omit<Note, 'id'> = {
    tenantId: input.tenantId,
    entityType: input.entityType,
    entityId: input.entityId,
    jobId: input.jobId,
    content: input.content,
    mentions: mentionedUserIds,
    source: input.source,
    createdBy: input.createdBy,
    createdByName: input.createdByName,
    // A reply is never independently pinned — it lives under its parent.
    pinned: input.parentNoteId ? false : input.pinned ?? false,
    pinnedAt: !input.parentNoteId && input.pinned ? now : undefined,
    pinnedBy: !input.parentNoteId && input.pinned ? input.createdBy : undefined,
    parentNoteId: input.parentNoteId,
    createdAt: now,
    updatedAt: now,
  };

  // Strip undefined fields — Firestore rejects them.
  const cleaned = Object.fromEntries(
    Object.entries(noteDoc).filter(([, v]) => v !== undefined),
  );

  const ref = await addDoc(collection(db, 'notes'), cleaned);

  // Keep the parent's reply counter roughly current so a collapsed thread can
  // say "3 replies" without every card subscribing to its children. Best
  // effort by design — the rendered count always comes from the actual reply
  // documents, so a failed increment costs a badge, never a message.
  if (input.parentNoteId) {
    void updateDoc(doc(db, 'notes', input.parentNoteId), {
      replyCount: increment(1),
    }).catch((err) => {
      console.warn('[notes] replyCount bump failed:', err);
    });
  }

  // Fire-and-forget mention notifications.
  if (input.availableUsers && mentionedUserIds.length > 0) {
    void createMentions({
      noteContent: input.content,
      entityType: toMentionEntityType(input.entityType),
      entityId: input.entityId,
      entityTitle: input.entityTitle || `${input.entityType}: ${input.entityId}`,
      createdBy: input.createdBy,
      createdByName: input.createdByName,
      tenantId: input.tenantId,
      linkPath: linkPathFor(input.entityType, input.entityId, input.jobId),
      sourceNoteId: ref.id,
      availableUsers: input.availableUsers,
    }).catch((err) => {
      console.error('[notes] mention fanout failed:', err);
    });
  }

  return ref.id;
}

// ============================================================================
// REPLIES / THREADING
// ============================================================================

export interface CreateNoteReplyInput {
  /** The note being replied to. Must already exist and carry an id. */
  parent: Note;
  content: string;
  createdBy: string;
  createdByName: string;
  source: NoteSource;
  entityTitle?: string;
  availableUsers?: MentionableUser[];
  /**
   * Everyone already in this thread (root author + previous repliers). They
   * get notified without needing to be @mentioned — that's what makes a reply
   * a reply. The actor is filtered out here, so callers can pass the raw list.
   */
  threadParticipants?: { userId: string; userName: string }[];
}

/**
 * Reply to a note.
 *
 * The reply is an ordinary note in the same entity, stamped with
 * `parentNoteId`. That's deliberate: every existing subscription, roll-up and
 * tenant filter keeps working untouched, and no new composite index is needed
 * (CLAUDE.md — `where('tenantId','in',…)` + `orderBy` is what costs an index,
 * and this adds neither).
 *
 * Replying to a reply re-targets the thread ROOT, so threads stay one level
 * deep and a late reply can never orphan itself under a collapsed child.
 *
 * Two notification paths fire, and they cannot double-tag:
 *   1. @mentions in the reply text (via createNote's normal fanout)
 *   2. thread participants — root author + everyone who has replied
 * Both write through `mentionDocId`, which is keyed on
 * (tenant, user, entity, sourceNoteId) — so a participant who is ALSO
 * @mentioned lands on the same document id and gets exactly one bell entry.
 *
 * @returns The new reply's document id
 */
export async function createNoteReply(input: CreateNoteReplyInput): Promise<string> {
  const { parent } = input;
  const rootId = parent.parentNoteId || parent.id;
  if (!rootId) {
    throw new Error('createNoteReply: parent note has no id');
  }

  const replyId = await createNote({
    tenantId: parent.tenantId,
    entityType: parent.entityType,
    entityId: parent.entityId,
    jobId: parent.jobId,
    content: input.content,
    source: input.source,
    createdBy: input.createdBy,
    createdByName: input.createdByName,
    entityTitle: input.entityTitle,
    availableUsers: input.availableUsers,
    parentNoteId: rootId,
  });

  // Notify the thread — minus whoever just wrote the reply.
  const participants = (input.threadParticipants ?? []).filter(
    (p) => p.userId && p.userId !== input.createdBy,
  );

  if (participants.length > 0) {
    void createDirectMentions({
      recipients: participants,
      noteContent: input.content,
      entityType: toMentionEntityType(parent.entityType),
      entityId: parent.entityId,
      entityTitle:
        input.entityTitle || `${parent.entityType}: ${parent.entityId}`,
      createdBy: input.createdBy,
      createdByName: input.createdByName,
      tenantId: parent.tenantId,
      linkPath: linkPathFor(parent.entityType, parent.entityId, parent.jobId),
      sourceNoteId: replyId,
    }).catch((err) => {
      console.error('[notes] reply participant fanout failed:', err);
    });
  }

  return replyId;
}

// ============================================================================
// UPDATE / DELETE
// ============================================================================

export async function updateNoteContent(
  noteId: string,
  content: string,
  availableUsers?: MentionableUser[],
): Promise<void> {
  const updates: Record<string, unknown> = {
    content,
    updatedAt: new Date().toISOString(),
  };
  if (availableUsers) {
    updates.mentions = Array.from(
      new Set(extractMentions(content, availableUsers).map((m) => m.userId)),
    );
  }
  await updateDoc(doc(db, 'notes', noteId), updates);
}

export async function togglePinNote(
  noteId: string,
  pinned: boolean,
  userId: string,
): Promise<void> {
  // Deliberately does NOT bump `updatedAt` — the pin action carries its own
  // audit fields (pinnedAt/pinnedBy), and the "edited" label on the note
  // surfaces compares updatedAt vs createdAt. Stamping updatedAt here made a
  // simple pin/unpin permanently read as a content edit.
  await updateDoc(doc(db, 'notes', noteId), {
    pinned,
    pinnedAt: pinned ? new Date().toISOString() : null,
    pinnedBy: pinned ? userId : null,
  });
}

/**
 * Soft-delete a note — keeps the doc for audit but hides it from the panel.
 *
 * Deleting a note does NOT delete its replies: they stay in the collection and
 * `buildNoteThreads` promotes them to roots once the parent stops appearing.
 * Silently swallowing someone else's reply because the thread starter removed
 * their own line is not a trade worth making.
 *
 * @param parentNoteId - Pass when deleting a REPLY so the parent's counter
 *                       comes back down.
 */
export async function deleteNote(
  noteId: string,
  userId: string,
  parentNoteId?: string,
): Promise<void> {
  await updateDoc(doc(db, 'notes', noteId), {
    deleted: true,
    deletedAt: new Date().toISOString(),
    deletedBy: userId,
    updatedAt: new Date().toISOString(),
  });

  if (parentNoteId) {
    void updateDoc(doc(db, 'notes', parentNoteId), {
      replyCount: increment(-1),
    }).catch((err) => {
      console.warn('[notes] replyCount decrement failed:', err);
    });
  }
}

// ============================================================================
// READ / SUBSCRIBE
// ============================================================================

/**
 * Subscribe to notes for a single entity.
 *
 * Sorted client-side (pinned first, then newest first) so we don't need a
 * composite index on (pinned, createdAt).
 */
export function subscribeNotesForEntity(
  tenantId: string,
  entityType: NoteEntityType,
  entityId: string,
  callback: (notes: Note[]) => void,
): () => void {
  const q = query(
    collection(db, 'notes'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('entityType', '==', entityType),
    where('entityId', '==', entityId),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const notes = snapshot.docs
        .map((d) => ({ id: d.id, ...d.data() } as Note))
        .filter((n) => !n.deleted)
        .sort(sortNotes);
      callback(notes);
    },
    (err) => {
      console.error('[notes] subscribeNotesForEntity error:', err);
      callback([]);
    },
  );
}

/**
 * One-shot read of the PINNED notes for an entity, newest-first.
 *
 * Exists because pinning moved to this collection but several consumers were
 * left reading the legacy `Deal.dealNotes[].pinned` array — which nothing
 * writes any more, so their "pinned" surfaces went permanently empty. Callers
 * that need a point-in-time copy of the pins (job creation snapshots, the
 * scheduler's deal preview) use this instead of the legacy array.
 *
 * `pinned` / `deleted` are filtered client-side deliberately: the three
 * `where` clauses match `subscribeNotesForEntity` exactly, so this needs no
 * new composite index (see CLAUDE.md — `npm run lint:indexes`).
 */
export async function fetchPinnedNotesForEntity(
  tenantId: string,
  entityType: NoteEntityType,
  entityId: string,
): Promise<Note[]> {
  if (!entityId) return [];
  const q = query(
    collection(db, 'notes'),
    where('tenantId', 'in', tenantIdReadUnion(tenantId)),
    where('entityType', '==', entityType),
    where('entityId', '==', entityId),
  );
  const snap = await getDocs(q);
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() } as Note))
    .filter((n) => n.pinned && !n.deleted)
    .sort(sortNotes);
}

/** A job's parent entity reference for notes roll-up purposes. */
export interface NoteParentRef {
  type: NoteEntityType;
  id: string;
}

/**
 * Derive ALL parent entities for a job — the READ set for notes roll-ups.
 * A job can carry more than one — e.g. a fault-deployment job whose fault
 * sits under a deal, or a PM-meeting Task that links its deal via
 * `pmMeetingForDealId`. Notes written against ANY of these should surface on
 * the job, so this deliberately includes snapshot-only fallbacks
 * (`pmMeetingForDealId`, `briefingHistory.faultId`) that the composer's WRITE
 * target (`deriveParentEntity` in useNotes) does not.
 *
 * Lives here rather than in the hook so services can reach it without
 * importing a React module.
 */
export function deriveParentEntities(job: Job | null): NoteParentRef[] {
  if (!job) return [];
  const j = job as Job & { serviceRequestId?: string };
  const out: NoteParentRef[] = [];

  const dealId = job.dealId || job.pmMeetingForDealId;
  if (dealId) out.push({ type: 'deal', id: dealId });

  const faultId = job.faultId || job.briefingHistory?.faultId;
  if (faultId) out.push({ type: 'fault', id: faultId });

  if (j.serviceRequestId) {
    out.push({ type: 'service_request', id: j.serviceRequestId });
  }

  return out;
}

/**
 * The fault a job is for, if any — same precedence as `deriveParentEntities`
 * (explicit link first, then the frozen handoff snapshot's copy).
 *
 * Exported so the tech job screen can open a LIVE subscription to that fault
 * without re-deriving the precedence and drifting from the notes roll-up.
 */
export function faultIdForJob(job: Job | null): string | null {
  if (!job) return null;
  return job.faultId || job.briefingHistory?.faultId || null;
}

/**
 * Normalize the parents argument into a deduped array. Refs without an id
 * are dropped.
 */
function normalizeParents(
  parents: NoteParentRef[] | null | undefined,
): NoteParentRef[] {
  if (!parents) return [];
  const seen = new Set<string>();
  const out: NoteParentRef[] = [];
  for (const p of parents) {
    if (!p || !p.id) continue;
    const key = `${p.type}:${p.id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Subscribe to the rolled-up notes for a job + ALL of its parent entities
 * (deal, fault, service request — a job can carry more than one, e.g. a
 * fault-deployment job whose fault sits under a deal). Used by the scheduler
 * side panel and the tech job detail.
 *
 * Implementation: opens one onSnapshot listener per entity (job + each
 * parent) and merges them on every update. The callback always receives a
 * single combined, sorted array.
 */
export function subscribeRolledUpNotes(
  tenantId: string,
  jobId: string,
  parents: NoteParentRef[] | null,
  callback: (notes: Note[]) => void,
): () => void {
  const parentList = normalizeParents(parents);
  let jobNotes: Note[] = [];
  const parentNotes = new Map<string, Note[]>();

  const emit = () => {
    // Dedup by doc id. The per-entity queries are disjoint today
    // (entityType/entityId pairs can't overlap), but a future query change
    // or a doc matching two parents would silently double-render notes —
    // id-wins-first is free insurance.
    const seen = new Set<string>();
    const combined = [
      ...jobNotes,
      ...Array.from(parentNotes.values()).flat(),
    ]
      .filter((n) => {
        const key = n.id || `${n.entityType}:${n.entityId}:${n.createdAt}:${n.content}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      })
      .sort(sortNotes);
    callback(combined);
  };

  const unsubs: Array<() => void> = [];

  unsubs.push(
    subscribeNotesForEntity(tenantId, 'job', jobId, (notes) => {
      jobNotes = notes;
      emit();
    }),
  );

  for (const parent of parentList) {
    const key = `${parent.type}:${parent.id}`;
    unsubs.push(
      subscribeNotesForEntity(tenantId, parent.type, parent.id, (notes) => {
        parentNotes.set(key, notes);
        emit();
      }),
    );
  }

  return () => {
    for (const unsub of unsubs) unsub();
  };
}

// ============================================================================
// SORTING
// ============================================================================

/**
 * Pinned first, then newest first. Stable for ties.
 *
 * Exported so every notes surface orders identically — a surface that
 * re-implements "newest first" on its own silently drops the pin priority.
 */
export function sortNotes(a: Note, b: Note): number {
  const aPinned = a.pinned ? 1 : 0;
  const bPinned = b.pinned ? 1 : 0;
  if (aPinned !== bPinned) return bPinned - aPinned;
  return b.createdAt.localeCompare(a.createdAt);
}
